import type { QueryOptions } from '@tanstack/react-query'
import type { ScopeKeyParameter } from '@wagmi/core/internal'
import {
  type SimulationErrorType,
  type SimulationParameters,
  type SimulationReturnType,
  simulation,
} from '../actions/simulation'
import type { TransactionItems } from '../factories'
import type { ExtendedConfig, UnionPartial } from '../types'
import { filterQueryOptions } from '../utils'

export type SimulationOptions<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined,
> = UnionPartial<SimulationParameters<config, transactions, chainId>> & ScopeKeyParameter

export function simulationQueryOptions<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined,
>(
  config: config,
  options: SimulationOptions<config, transactions, chainId> = {} as any,
): QueryOptions<
  SimulationQueryFnData,
  SimulationErrorType,
  SimulationData,
  SimulationQueryKey<config, transactions, chainId>
> {
  return {
    async queryFn({ queryKey }) {
      const { transactions, connector } = options
      const { scopeKey: _, ...parameters } = queryKey[1]
      return simulation(config, {
        transactions,
        connector,
        ...(parameters as any),
      })
    },
    queryKey: simulationQueryKey(options),
  } as const
}

export type SimulationQueryFnData = SimulationReturnType

export type SimulationData = SimulationQueryFnData

export function simulationQueryKey<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined,
>(options: SimulationOptions<config, transactions, chainId> = {} as any) {
  const { connector: _c, value, ...rest } = options
  return ['simulation', filterQueryOptions({ ...rest, value: value?.toString() })] as const
}

export type SimulationQueryKey<
  config extends ExtendedConfig,
  transactions extends TransactionItems,
  chainId extends config['chains'][number]['id'] | undefined,
> = ReturnType<typeof simulationQueryKey<config, transactions, chainId>>
